import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  BookOpenCheck,
  ShieldCheck,
  MessagesSquare,
  ChevronDown,
  FileSearch,
  Play,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import Header from "./Header";
import Footer from "./Footer";
import SEO from "./SEO";
import LazySection from "./LazySection";
import VideoWidget from "./VideoWidget";
import LandingResearchPreview from "./LandingResearchPreview";
import "./LegacyLandingPage.css";

const MOBILE_LANDING_BREAKPOINT = 768;

const ANON_DEV_ROTATING_WORDS = [
  "Arguments.",
  "Briefs.",
  "Case Strategy.",
  "Research.",
];

const LegacyLandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { darkMode } = useTheme();
  const isAnonymous = !isAuthenticated;
  const showAnonHero = isAnonymous;
  const modeSectionRef = useRef(null);
  const [faqOpen, setFaqOpen] = useState(false);
  const [rotatingWordIndex, setRotatingWordIndex] = useState(0);
  const [isMobileLanding, setIsMobileLanding] = useState(
    window.innerWidth < MOBILE_LANDING_BREAKPOINT,
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobileLanding(window.innerWidth < MOBILE_LANDING_BREAKPOINT);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (!showAnonHero) return undefined;
    const id = setInterval(() => {
      setRotatingWordIndex((i) => (i + 1) % ANON_DEV_ROTATING_WORDS.length);
    }, 2200);
    return () => clearInterval(id);
  }, [showAnonHero]);

  const scrollToModeSection = () => {
    modeSectionRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const features = [
    {
      icon: <BookOpenCheck className="feature-icon" size={40} />,
      title: "Multi-AI Collaboration Engine",
      desc: (
        <>
          LawFusion orchestrates{" "}
          <strong>ChatGPT, Claude, Gemini, and Perplexity</strong> to analyze
          your legal question together. Each model challenges the others'
          reasoning, bringing <strong>diverse perspectives</strong> that{" "}
          <strong>strengthen your arguments</strong> beyond what{" "}
          <strong>any single AI</strong> can deliver.
        </>
      ),
      hoverClass: "feature-card-1",
    },
    {
      icon: <MessagesSquare className="feature-icon" size={40} />,
      title: "AI Debate Mode",
      desc: (
        <>
          <strong>Multiple AIs automatically argue both sides</strong> of your
          legal issue ΓÇö <strong>surfacing weaknesses</strong>,{" "}
          <strong>stress-testing your theory</strong>, and uncovering overlooked
          angles so you can <strong>crush opposing arguments</strong> with
          confidence. It's like having a{" "}
          <strong>private moot-court panel</strong> refining your arguments
          before opposing counsel ever sees them.
        </>
      ),
      hoverClass: "feature-card-2",
    },
    {
      icon: <ShieldCheck className="feature-icon" size={40} />,
      title: "Intelligence Multiplier",
      desc: (
        <>
          By combining <strong>five distinct AI perspectives</strong>, LawFusion
          reveals{" "}
          <strong>
            strategies and legal arguments you wouldn't find alone
          </strong>
          . Their insights are synthesized into a{" "}
          <strong>unified, stronger answer</strong> that elevates your reasoning
          ΓÇö giving solo and small-firm attorneys research depth traditionally
          seen in <strong>biglaw teams</strong>.
        </>
      ),
      hoverClass: "feature-card-3",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const renderLandingSection = (
    section,
    options = { threshold: 0.1, rootMargin: "250px" },
  ) => {
    if (isMobileLanding) return section;
    return <LazySection options={options}>{section}</LazySection>;
  };

  return (
    <div
      className={`landing-page ${darkMode ? "dark" : ""} ${isMobileLanding ? "mobile-instant" : ""} landing-env-dev-only${showAnonHero ? " landing-anon-dev-hero" : ""}`}
    >
      <SEO
        path="/"
        description="LawFusion uses multiple AI models ΓÇö ChatGPT, Claude, Gemini, Grok, Perplexity ΓÇö to stress-test legal arguments through structured debate and multi-perspective research. Built for solo practitioners and small law firms."
      />
      <Header />
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content-wrapper">
          {showAnonHero ? (
            <>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
                className="hero-title hero-title--anon-dev"
              >
                Outmaneuver Opposing Counsel
                <br />
                With Stronger
                <br />
                <span className="hero-rotating-wrapper">
                  <span className="hero-rotating-sizer" aria-hidden="true">
                    Case Strategy.
                  </span>
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={ANON_DEV_ROTATING_WORDS[rotatingWordIndex]}
                      className="hero-rotating-word"
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                    >
                      {ANON_DEV_ROTATING_WORDS[rotatingWordIndex]}
                    </motion.span>
                  </AnimatePresence>
                </span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="hero-subtitle hero-subtitle--dev"
              >
                LawFusion runs your legal questions, briefs, and case strategy
                through multiple AIs ΓÇö comparing analysis, debating arguments,
                exposing weak spots, and helping you get ahead of opposing
                counsel.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                className="hero-guarantee-wrap"
              >
                <div className="hero-guarantee-box hero-guarantee-box--pill">
                  <ShieldCheck
                    className="hero-guarantee-icon"
                    strokeWidth={2.2}
                    aria-hidden="true"
                  />
                  <p>
                    Guaranteed to strengthen your argument ΓÇö or your money back.
                  </p>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.8 }}
                className="hero-cta-wrap"
              >
                <motion.button
                  type="button"
                  className="btn-primary btn-hero-anon-dev"
                  onClick={scrollToModeSection}
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.97 }}
                >
                  See How It Works
                  <ArrowRight className="btn-icon" />
                </motion.button>
              </motion.div>
            </>
          ) : (
            <>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
                className="hero-title hero-title--dev"
              >
                Strengthen Your Case Before Opposing Counsel Attacks It.
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="hero-subtitle hero-subtitle--dev"
              >
                LawFusion runs your legal questions, briefs, and case strategy
                through multiple AIs ΓÇö comparing analysis, debating arguments,
                exposing weak spots, and helping you get ahead of opposing
                counsel.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                className="hero-guarantee-wrap"
              >
                <div className="hero-guarantee-box">
                  <ShieldCheck
                    className="hero-guarantee-icon"
                    strokeWidth={2.2}
                    aria-hidden="true"
                  />
                  <p>
                    Guaranteed to strengthen your argument ΓÇö or your money back.
                  </p>
                </div>
              </motion.div>
            </>
          )}
        </div>
      </section>

      {/* Divider */}
      <div className="divider"></div>

      {/* How It Works ΓÇö anon + dev only */}
      {showAnonHero &&
        renderLandingSection(
          <section
            className="landing-mode-section"
            id="mode-section"
            ref={modeSectionRef}
          >
            <div className="landing-mode-section-inner">
              <p className="landing-mode-label">How It Works</p>
              <h2 className="landing-mode-title">
                Choose How You Want to Strengthen Your Case.
              </h2>
              <div className="landing-mode-cards">
                <div className="landing-mode-card">
                  <span className="landing-mode-card-badge">Research Mode</span>
                  <h3>Multi-AI Legal Research</h3>
                  <p>
                    Run your legal question through multiple AI models
                    simultaneously. LawFusion compares the responses, highlights
                    agreement and disagreement, surfaces gaps, and helps you
                    identify the strongest path forward.
                  </p>
                  <div className="landing-mode-card-ctas">
                    <button
                      type="button"
                      className="btn-primary landing-mode-btn-primary"
                      onClick={() => navigate("/research")}
                    >
                      Try Research Mode Free
                      <ArrowRight className="btn-icon" />
                    </button>
                    <button
                      type="button"
                      className="landing-mode-btn-secondary"
                      onClick={() => navigate("/research")}
                    >
                      <Play className="landing-mode-play-icon" size={14} />
                      Watch Research Demo
                    </button>
                  </div>
                </div>
                <div className="landing-mode-card">
                  <span className="landing-mode-card-badge">Debate Mode</span>
                  <h3>Adversarial Argument Testing</h3>
                  <p>
                    LawFusion assigns AI roles ΓÇö Advocate, Critic, and Strategist
                    ΓÇö then runs a structured debate to expose weak spots before
                    opposing counsel does.
                  </p>
                  <div className="landing-mode-card-ctas">
                    <button
                      type="button"
                      className="btn-primary landing-mode-btn-primary"
                      onClick={() => navigate("/debate")}
                    >
                      Try Debate Mode Free
                      <ArrowRight className="btn-icon" />
                    </button>
                    <button
                      type="button"
                      className="landing-mode-btn-secondary"
                      onClick={() => navigate("/debate")}
                    >
                      <Play className="landing-mode-play-icon" size={14} />
                      Watch Debate Demo
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>,
          { threshold: 0.1, rootMargin: "250px" },
        )}

      {/* Research Mode UI preview ΓÇö anon + dev only */}
      {showAnonHero &&
        renderLandingSection(<LandingResearchPreview />, {
          threshold: 0.1,
          rootMargin: "250px",
        })}

      {/* Debate Mode video ΓÇö dev / dev.lawfusion.ai only (logged-in dev users) */}
      {!showAnonHero && renderLandingSection(
        <section className="landing-video-section">
          <div className="landing-video-frame">
            <VideoWidget
              mode="always-open"
              accentColor="red"
              cornerBadge="DEBATE MODE"
              title="Debate Mode Demo"
              subtitle="Watch your argument get attacked"
              videoSrc=""
              mockPanel={
                <div className="landing-mock landing-mock--debate">
                  <div className="landing-mock__header">
                    <span className="landing-mock__header-dot" />
                    <span className="landing-mock__header-text">
                      DEBATE MODE ΓÇö IRAC ANALYSIS
                    </span>
                  </div>
                  <div className="landing-mock__columns landing-mock__columns--debate">
                    <div className="landing-mock__col landing-mock__col--blue">
                      <div className="landing-mock__col-title">CLAUDE</div>
                      <div className="landing-mock__col-sub">Round 1 ┬╖ Issue</div>
                      <div className="landing-mock__lines">
                        <span /><span /><span /><span style={{ width: "70%" }} />
                      </div>
                    </div>
                    <div className="landing-mock__col landing-mock__col--red">
                      <div className="landing-mock__col-title">CHATGPT</div>
                      <div className="landing-mock__col-sub">Round 1 ┬╖ Rule</div>
                      <div className="landing-mock__lines">
                        <span /><span /><span /><span style={{ width: "55%" }} />
                      </div>
                    </div>
                    <div className="landing-mock__col landing-mock__col--green">
                      <div className="landing-mock__col-title">GEMINI</div>
                      <div className="landing-mock__col-sub">Round 1 ┬╖ Application</div>
                      <div className="landing-mock__lines">
                        <span /><span /><span /><span style={{ width: "65%" }} />
                      </div>
                    </div>
                  </div>
                  <div className="landing-mock__footer">
                    Consensus summary streaming from ClaudeΓÇª
                  </div>
                </div>
              }
            />
          </div>
          <div className="landing-video-cta">
            <motion.button
              className="btn-primary"
              onClick={() => navigate("/debate")}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
            >
              Try it on your case
              <ArrowRight className="btn-icon" />
            </motion.button>
            <span className="landing-video-hint">
              Free ┬╖ 1 round ┬╖ No login required
            </span>
          </div>
        </section>,
        { threshold: 0.1, rootMargin: "250px" },
      )}

      {/* Features Section */}
      {renderLandingSection(
        <motion.section
          className="features-section"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {features.map((feature, i) => (
            <motion.div
              key={i}
              variants={itemVariants}
              className={`feature-card ${feature.hoverClass}`}
              whileHover={{
                y: -10,
                transition: { duration: 0.3 },
              }}
            >
              <motion.div
                animate={{
                  y: [0, -8, 0],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  delay: i * 0.3,
                  ease: "easeInOut",
                }}
              >
                {feature.icon}
              </motion.div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-desc">{feature.desc}</p>
            </motion.div>
          ))}
        </motion.section>,
        { threshold: 0.1, rootMargin: "250px" },
      )}

      {/* Comparative Section */}
      {renderLandingSection(
        <motion.section
          className="comparative-section"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
        >
          <motion.h2
            className="comparative-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Research Smarter. Argue Stronger.
          </motion.h2>
          <motion.p
            className="comparative-subtitle"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Designed for legal professionals. Powered by multiple AIs. Built for
            clarity and confidence.
          </motion.p>
          {!showAnonHero && (
          <>
          <motion.p
            className="comparative-eyebrow"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            RESEARCH MODE
            <br />
            <strong>Start with a simple analysis ΓÇö no login required</strong>
          </motion.p>
          <div className="landing-video-frame landing-video-frame--inverse">
            <VideoWidget
              mode="always-open"
              accentColor="red"
              cornerBadge="RESEARCH MODE"
              title="Research Mode Demo"
              subtitle="See what most attorneys miss"
              videoSrc=""
              mockPanel={
                <div className="landing-mock landing-mock--research">
                  <div className="landing-mock__header landing-mock__header--research">
                    <span className="landing-mock__header-text">
                      MULTI-MODEL RESEARCH ┬╖ IRAC SYNTHESIS
                    </span>
                  </div>
                  <div className="landing-mock__row">
                    <div className="landing-mock__row-label">CHATGPT</div>
                    <div className="landing-mock__row-lines">
                      <span /><span style={{ width: "80%" }} />
                    </div>
                  </div>
                  <div className="landing-mock__row">
                    <div className="landing-mock__row-label">CLAUDE</div>
                    <div className="landing-mock__row-lines">
                      <span /><span style={{ width: "72%" }} />
                    </div>
                  </div>
                  <div className="landing-mock__row">
                    <div className="landing-mock__row-label">GEMINI</div>
                    <div className="landing-mock__row-lines">
                      <span /><span style={{ width: "65%" }} />
                    </div>
                  </div>
                </div>
              }
            />
          </div>
          </>
          )}
          {!showAnonHero && (
          <motion.div
            className="comparative-free-cta"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <motion.button
              className="btn-secondary"
              whileHover={{
                scale: 1.05,
                boxShadow: "0 10px 25px -5px rgba(255, 255, 255, 0.3)",
              }}
              onClick={() => {
                navigate("/research");
              }}
            >
              Run a free analysis
              <ArrowRight className="btn-icon" />
            </motion.button>
          </motion.div>
          )}
        </motion.section>,
        { threshold: 0.1, rootMargin: "250px" },
      )}

      {/* Trusted By Section */}
      {renderLandingSection(<motion.section
        className="trusted-by-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8 }}
      >
        <div className="trusted-by-container">
          <motion.div
            className="trusted-by-content"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <img
              src={`${process.env.PUBLIC_URL}/lp.png`}
              alt="Legal Professionals"
              className="trusted-by-image"
            />
            <div className="trusted-by-text-line">Trusted by</div>
            <div className="trusted-by-text-line">legal professionals</div>
          </motion.div>
          <motion.div
            className="trusted-by-box"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="trusted-by-icon">
              <FileSearch size={50} color="#ffffff" strokeWidth={2} />
            </div>
            <div className="trusted-by-stat">Hundreds</div>
            <div className="trusted-by-stat-label">
              AI Debates & Analyses
              <br />
              Completed
            </div>
          </motion.div>
        </div>
      </motion.section>, { threshold: 0.1, rootMargin: "250px" })}

      {/* Powered By Section */}
      {renderLandingSection(<motion.section
        className="powered-by-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8 }}
      >
        <motion.p
          className="powered-by-text"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Powered by leading AI systems
        </motion.p>
        <motion.div
          className="powered-by-logos"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {[
            {
              src: `/openai-icon.svg`,
              alt: "ChatGPT / OpenAI",
              caption: "ChatGPT",
              delay: 0,
            },
            {
              src: `https://cdn.simpleicons.org/claude/${darkMode ? "ffffff" : "000000"}`,
              alt: "Claude",
              caption: "Claude",
              delay: 0.1,
            },
            {
              src: `https://cdn.simpleicons.org/googlegemini/${darkMode ? "ffffff" : "000000"}`,
              alt: "Google Gemini",
              caption: "Gemini",
              delay: 0.2,
            },
            {
              src: `https://cdn.simpleicons.org/perplexity/${darkMode ? "ffffff" : "000000"}`,
              alt: "Perplexity AI",
              caption: "Perplexity",
              delay: 0.3,
            },
            {
              src: `/grok-icon.jpeg`,
              alt: "Grok (xAI)",
              caption: "Grok",
              delay: 0.4,
            },
          ].map((logo, i) => (
            <motion.div
              key={i}
              className="logo-item"
              variants={itemVariants}
              whileHover={{
                scale: 1.15,
                y: -5,
                transition: { duration: 0.2 },
              }}
            >
              <img
                src={logo.src}
                alt={logo.alt}
                className="ai-logo"
                onError={(e) => {
                  if (logo.alt === "Claude") {
                    e.currentTarget.src = `https://cdn.simpleicons.org/anthropic/${darkMode ? "ffffff" : "000000"}`;
                  }
                }}
              />
              <span className="ai-logo-caption">{logo.caption}</span>
            </motion.div>
          ))}
        </motion.div>
      </motion.section>, { threshold: 0.1, rootMargin: "250px" })}

      {/* FAQ Section */}
      {renderLandingSection(<motion.section
        className="faq-section"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <motion.div
          onClick={() => setFaqOpen(!faqOpen)}
          className="faq-header"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          transition={{ type: "spring", stiffness: 400, damping: 17 }}
        >
          <motion.h3
            className="faq-title"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Why use LawFusion instead of Westlaw or LexisNexis?
          </motion.h3>
          <motion.div
            animate={{
              rotate: faqOpen ? 180 : 0,
              scale: faqOpen ? 1.1 : 1,
            }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 20,
              duration: 0.4,
            }}
          >
            <ChevronDown className="faq-chevron" />
          </motion.div>
        </motion.div>

        <AnimatePresence mode="wait">
          {faqOpen && (
            <motion.div
              initial={{
                opacity: 0,
                height: 0,
                y: -20,
                scale: 0.95,
              }}
              animate={{
                opacity: 1,
                height: "auto",
                y: 0,
                scale: 1,
              }}
              exit={{
                opacity: 0,
                height: 0,
                y: -10,
                scale: 0.95,
              }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 30,
                mass: 0.8,
                opacity: { duration: 0.2 },
                height: { duration: 0.4 },
              }}
              className="faq-content"
            >
              <motion.p
                className="faq-text"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1, duration: 0.5, ease: "easeOut" }}
              >
                Westlaw and LexisNexis are expensive databases
                ($500ΓÇô$2,000/month per attorney) that dump hundreds of search
                results on you and make you do all the analytical work. You
                still spend hours manually reading, synthesizing, and spotting
                patterns.
              </motion.p>
              <motion.p
                className="faq-text"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.5, ease: "easeOut" }}
              >
                <strong>LawFusion does the analysis for you.</strong> We run
                your question through multiple leading AI models simultaneously,
                compare their interpretations, and synthesize the insights into
                actionable intelligence. Our <strong>Debate Mode</strong> goes
                furtherΓÇöAIs argue both sides of your issue, stress-testing your
                theory and exposing vulnerabilities before you face opposing
                counsel. And because multiple AIs collaborate,
                <strong>
                  {" "}
                  you discover arguments and strategic angles that single-AI
                  tools ΓÇö and single attorneys ΓÇö would never find alone.
                </strong>
              </motion.p>
              <motion.p
                className="faq-text faq-text-highlight"
                initial={{ opacity: 0, x: -20, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                transition={{ delay: 0.3, duration: 0.6, ease: "easeOut" }}
                whileInView={{
                  scale: [1, 1.02, 1],
                }}
                viewport={{ once: true }}
              >
                Result: sophisticated legal analysis in minutes instead of
                hoursΓÇöat a fraction of the cost.
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.section>, { threshold: 0.1, rootMargin: "250px" })}

      {/* Divider */}
      <div className="divider"></div>

      {/* Pricing + CTA */}
      {renderLandingSection(<motion.section
        className="pricing-cta-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8 }}
      >
        <motion.h2
          className="pricing-title"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Simple, Transparent Pricing
        </motion.h2>
        <motion.p
          className="pricing-subtitle"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          Start with a 7-day free trial. No long-term contracts. Cancel anytime.
        </motion.p>
        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <motion.button
            className="btn-primary btn-large"
            onClick={() => navigate("/pricing")}
            whileHover={{
              scale: 1.05,
              boxShadow:
                "0 20px 25px -5px rgba(37, 99, 235, 0.4), 0 10px 10px -5px rgba(37, 99, 235, 0.2)",
            }}
            whileTap={{ scale: 0.98 }}
          >
            View Pricing Plans
            <motion.span
              animate={{ x: [0, 5, 0] }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <ArrowRight className="btn-icon" />
            </motion.span>
          </motion.button>
        </motion.div>
      </motion.section>, { threshold: 0.1, rootMargin: "250px" })}

      <Footer disableLazy={isMobileLanding} />
    </div>
  );
};

export default LegacyLandingPage;
